import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslate } from '@tolgee/react';
import { useAuthService } from "../../Context/AuthContext";
import moment from "moment";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { getGenderList, mapGender } from "../../Constants/DropdownValues";
import BirthdayMenu from "../Auth Components/BirthdayMenu";
import { ReactComponent as Delete } from "../../Assets/iconsv2/settings/delete.svg";
import { ReactComponent as Arrow } from "../../Assets/iconsv2/generic/forwardArrow.svg";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import SettingsElement from "./SettingsElement";
import DeleteConfirmationChild from "../GenericComponents/DeleteConfirmationChild";
import DeleteReasonsModal from "../ProfileComponents/DeleteReasonsModal";
import { displayUserAvatar } from "../../Helpers/chatHelper/AvatarHelper";
import Select from "react-select";
import { ReactComponent as ProIcon } from "../../Assets/iconsv2/generic/PremiumUser.svg";
import { useNotify } from "../../Helpers/Notifications";
import { ReactComponent as PlatformIcon } from "../../Assets/iconsv2/generic/BusinessUser.svg";
import { ReactComponent as QuestionIcon } from "../../Assets/iconsv2/generic/Question.svg";
import LinkPhoneNumberModal from "./LinkPhoneNumberModal";
import LinkEmailModal from "./LinkEmailModal.js";
import { ReactComponent as PlatformIconLight } from "../../Assets/iconsv2/generic/BusinessUserLight.svg";
import InfoModal from "./InfoModal.js";
import IconButton from "../GenericComponents/IconButton.js";

const AccountSettings = () => {

  const isElectron = !!window.electron;
  const { notifySuccess, notifyError, notifyInfo } = useNotify();
  const { user, updateUser, updateUserProfilePictures, deleteUser } =
    useAuthService();
  const {
    setIsSubscriptionModalOpen,
    setLoading,
    deleteReasonModalOpen,
    setDeleteReasonModalOpen,
    theme,
    isInfoModalOpen,
    setIsInfoModalOpen,
    isLinkPhoneInfoModalOpen, setIsLinkPhoneInfoModalOpen
  } = useGenericComponentsStateService();

  const { t } = useTranslate();

  const [name, setName] = useState(user.name);
  const [about, setAbout] = useState(user.about || "");
  const [birthdate, setBirthdate] = useState(user.birthdate);
  const [gender, setGender] = useState(mapGender(user.gender, t));
  const [culturalBackground, setCulturalBackground] = useState(
    user.cultural_background
  );
  const [infoChanged, setInfoChanged] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const themeProps = useTheme();
  const profileAvatarRef = useRef();
  const [isLinkPhoneNumberOpen, setIsLinkPhoneNumberOpen] = useState(false);
  const [isLinkEmailOpen, setIsLinkEmailOpen] = useState(false);
  const [infoType, setInfoType] = useState("cultural");
  const selectedCustomStyles = {
    container: (provided) => ({
      ...provided,
      "&:focus-visible": {
        outline: "none",
      },
      width: "100%",
      fontSize: "14px",
      color: themeProps.menu.primaryTextColor,
    }),

    control: (provided) => ({
      ...provided,
      "&:focus-visible": {
        outline: "none",
      },
      "&:selected": {
        outline: "none",
      },
      "&:hover": {
        boxShadow: "none",
      },

      boxShadow: "none",
      borderColor: themeProps.menu.groupContentBackground,
      background: themeProps.menu.groupContentBackground,
      color: themeProps.menu.primaryTextColor,
      outline: "none",
    }),
    menuList: (provided) => ({
      ...provided,
      background: themeProps.menu.groupContentBackground,
      color: themeProps.menu.primaryTextColor,
    }),
    option: (provided, { isSelected }) => ({
      ...provided,
      "&:hover": {
        background: themeProps.menu.enterPhoneNumberBackground,
        color: themeProps.menu.selectedTextColor,
      },
      "&:active": {
        background: themeProps.menu.groupContentBackground,
      },
      "&:selected": {
        background: themeProps.menu.primaryTextColor,
      },
      background: isSelected ? themeProps.menu.selectedTextColor : "inherit",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "16px",
      lineHeight: "22px",
      fontWeight: "400",
      opacity: "0.6",
      color: themeProps.menu.primaryTextColor,
      margin: 0,
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "16px",
      fontWeight: "500",
      color: themeProps.menu.primaryTextColor,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),

    indicatorContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),
  };

  const handleOpenSubscriptionModal = () => {
    setIsSubscriptionModalOpen(true);
  };

  const handleOpenAvatar = () => {
    profileAvatarRef.current.click();
  };

  const openDeleteReasonModal = () => {
    setDeleteReasonModalOpen(true);
  };

  const closeDeleteReasonModal = () => {
    setDeleteReasonModalOpen(false);
    setSelectedReasons([]);
  };

  const checkInfoChanged = () => {
    const initial = moment(user.birthdate);
    const second = moment(birthdate);
    let comparedDates = true;

    if (initial.isValid() || second.isValid()) {
      comparedDates = initial.startOf("day").isSame(second.startOf("day"));
    }
    if (
      about !== user.about ||
      culturalBackground !== user.cultural_background ||
      name !== user.name ||
      gender?.value != user.gender ||
      !comparedDates
    ) {
      setInfoChanged(true);
    } else {
      setInfoChanged(false);
    }
  };

  const handleAvatarChange = (e) => {
    const image = e.target.files[0];
    if(!image){
      e.target.value = "";
      return;
    }
    const data = new FormData();
    data.append("image", image);
    updateUserProfilePictures(
      data,
      function (response) {
        notifySuccess(t('profile.profile_picture_changed'));
      },
      function (error) {
        notifyError(`${t('general.something_wrong')}`);
        console.log(error);
      }
    );
    e.target.value = "";
  };

  const handleUpdateUser = () => {
    const initial = moment(user.birthdate);
    const second = moment(birthdate);
    let comparedDates = initial.startOf("day").isSame(second.startOf("day"));
    var objectData = {};
    if (user.country_code) {
      objectData.country_code = user.country_code;
    }
    if (about !== user.about) {
      objectData.about = about;
    }

    if (name !== user.name) {
      objectData.name = name;
    }

    if (culturalBackground !== user.cultural_background) {
      objectData.cultural_background = culturalBackground;
    }

    if (gender?.value != user.gender) {
      objectData.gender = gender.value;
    }

    if (!comparedDates) {
      objectData.birthdate = moment(birthdate).format("yyyy-MM-DD");
    }

    updateUser(
      objectData,
      function (response) {
        notifySuccess(`${t('profile.profile_updated')}`);
        setInfoChanged(false);
      },
      function (error) {
        console.log(error, "error");
        notifyError(`${t('general.something_wrong')}`);
      }
    );
  };

  const handleOpenInfoModal = (modalInfoType) => {
    setInfoType(modalInfoType);
    setIsInfoModalOpen(true);
  };

  const handleDeleteAccount = () => {
    let reasons = selectedReasons.map((item) => {
      return item.reason;
    });
    setLoading(true);

    deleteUser(
      reasons,
      (response) => {
        setDeleteReasonModalOpen(false);
        setLoading(false);
      },
      (error) => {
        notifyError(`${t('general.something_wrong')}`);
        console.log(error);
        setLoading(false);
      }
    );
  };

  function auto_grow(element) {
    element.style.height = "5px";
    element.style.height = element.scrollHeight + "px";
  }
  const handleOpenLinkPhoneNumberModal = () => {
    if (isElectron) {

      setIsLinkPhoneInfoModalOpen(true)
    } else {
      setIsLinkPhoneNumberOpen(true)
    }

  }
  useEffect(() => {
    let element = document.getElementById("resizeTa");
    let element2 = document.getElementById("resizeTa-2");

    auto_grow(element);
    auto_grow(element2);
  }, [about, culturalBackground]);

  useEffect(() => {
    setName(user.name);
    setAbout(user.about);
    setCulturalBackground(user.cultural_background);
    setGender(mapGender(user.gender, t));
    setBirthdate(user.birthdate);
  }, [user, t]);

  useEffect(() => {
    checkInfoChanged();
  }, [name, about, culturalBackground, birthdate, gender]);

  return (
    <Container>
      {isInfoModalOpen && (
        <InfoModal
          infoType={infoType}
          openModal={() => setIsInfoModalOpen(false)}
        />
      )}

      {isLinkPhoneInfoModalOpen && (
        <InfoModal
          infoType={"link_phone"}
          openModal={() => setIsLinkPhoneInfoModalOpen(false)}
        />
      )}
      {deleteReasonModalOpen && (
        <DeleteReasonsModal
          handleClose={closeDeleteReasonModal}
          setDeleteConfirmationOpen={setDeleteConfirmationOpen}
          setSelectedReasons={setSelectedReasons}
          selectedReasons={selectedReasons}
        />
      )}
      {isLinkPhoneNumberOpen && (
        <LinkPhoneNumberModal
          closeLinkPhoneNumberModal={() => setIsLinkPhoneNumberOpen(false)}
        />
      )}

      {isLinkEmailOpen && (
        <LinkEmailModal closeLinkEmailModal={() => setIsLinkEmailOpen(false)} />
      )}

      <Header>
        <HeaderTitle>{t('general.account')}</HeaderTitle>
        {infoChanged && (
          <PrimaryButton
            onClick={() => handleUpdateUser()}
            text={t('general.save')}
            disabled = {!name || name.trim() === ""}
          />
        )}
      </Header>
      <ContentWrapper>
        <Content>
          <Group>
            <GroupTitle>
              {t('profile.account_details').toUpperCase()}
            </GroupTitle>
            <GroupContentWrapper>
              <UserOption>
                <UserAvatar>
                  <AvatarWrapper>
                    {displayUserAvatar(user)}
                    {user.is_pro_from_platform ? (
                      <ProWrapper>
                        {theme === "light" ? (
                          <PlatformIconLight />
                        ) : (
                          <PlatformIcon />
                        )}
                      </ProWrapper>
                    ) : (
                      user.is_pro && (
                        <ProWrapper>
                          <ProIcon />
                        </ProWrapper>
                      )
                    )}
                  </AvatarWrapper>
                  <EditButton onClick={() => handleOpenAvatar()}>
                    {t('general.edit').toUpperCase()}
                  </EditButton>
                </UserAvatar>
                <CustomInput
                  style={{ marginTop: "16px" }}
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  name="name"
                  maxLength={100}
                ></CustomInput>
              </UserOption>
              <Status>
                <CustomTextArea
                  id="resizeTa-2"
                  placeholder={t('profile.type_your_status')}
                  value={about}
                  onChange={(e) => setAbout(e.currentTarget.value)}
                  name="about"
                  maxLength={280}
                />
              </Status>
            </GroupContentWrapper>
          </Group>
          <Group>
            <Line>
              <GroupTitle>
                <GroupTitle>{t('general.details').toUpperCase()}</GroupTitle>
              </GroupTitle>
              <StyledInfoButton
                icon={<QuestionIcon />}
                onClick={() => handleOpenInfoModal("details")}
              />
            </Line>
            <GroupContentWrapper>
              <OptionWithPicker>
                <OptionWithPickerText>
                  {t('profile.birthday')}
                </OptionWithPickerText>
                <BirthdayMenu
                  slim={true}
                  format={"yyyy-dd-MM"}
                  value={birthdate}
                  noCalendar
                  setValue={setBirthdate}
                />
              </OptionWithPicker>
              <Separator />
              <OptionWithPicker style={{ padding: "0" }}>
                <OptionWithPickerText>
                  {t('profile.gender')}
                </OptionWithPickerText>
                <Select
                  value={gender}
                  onChange={(e) => setGender(e)}
                  options={getGenderList(t)}
                  isSearchable={false}
                  isOptionSelected={(e) => gender?.value === e.value}
                  styles={selectedCustomStyles}
                  placeholder={t('profile.please_select_gender')}
                ></Select>
              </OptionWithPicker>
            </GroupContentWrapper>
          </Group>

          <Group>
            <Line>
              <GroupTitle>
                {t('profile.cultural_background').toUpperCase()}
              </GroupTitle>
              <StyledInfoButton
                icon={<QuestionIcon />}
                onClick={() => handleOpenInfoModal("cultural")}
              />
            </Line>

            <GroupContentWrapper>
              <CustomTextArea
                id="resizeTa"
                placeholder={
                  t('profile.type_cultural_background')
                }
                value={culturalBackground}
                onChange={(e) => setCulturalBackground(e.currentTarget.value)}
                name="cultural_background"
                maxLength={1000}
              ></CustomTextArea>
            </GroupContentWrapper>
          </Group>
          {user.company_account_name && (
            <>
              <Separator />
              <Group>
                <GroupTitle>{t('settings.company').toUpperCase()}</GroupTitle>
                <CompanyDetails>
                  {t('settings.company_description')}{" "}
                  <BusinessText>{user.company_account_name}</BusinessText>{" "}
                  {t('settings.company_description2')}
                </CompanyDetails>
              </Group>
            </>
          )}
          <Separator />
          <Group>
            <GroupTitle>{t('settings.management').toUpperCase()}</GroupTitle>
            <GroupSubtitle>
              {t('settings.management_subtitle')}
            </GroupSubtitle>
          </Group>
          <GroupContentWrapper>
            <OptionWithPicker>
              <OptionWithPickerText>
                {t('profile.phone')}
              </OptionWithPickerText>
              {user.phone_number && (
                <CustomInput value={user.phone_number} readOnly />
              )}
              {!user.phone_number && (
                <LinkPhoneNumberBtn
                  onClick={() => handleOpenLinkPhoneNumberModal()}
                >
                  {t('settings.link_phone_number')}
                </LinkPhoneNumberBtn>
              )}
              {user.phone_number && (
                <LinkPhoneNumberBtn
                  onClick={() => handleOpenLinkPhoneNumberModal()}
                >
                  {t('settings.change')}
                </LinkPhoneNumberBtn>
              )}
            </OptionWithPicker>

            <OptionWithPicker>
              <OptionWithPickerText>
                {t('profile.email')}
              </OptionWithPickerText>
              {user.email && <CustomInput value={user.email} readOnly />}
              {!user.email && (
                <LinkPhoneNumberBtn onClick={() => setIsLinkEmailOpen(true)}>
                  {t('settings.link_email')}
                </LinkPhoneNumberBtn>
              )}
              {user.email && (
                <LinkPhoneNumberBtn onClick={() => setIsLinkEmailOpen(true)}>
                  {t('settings.change')}
                </LinkPhoneNumberBtn>
              )}
            </OptionWithPicker>

            <Separator />
            <OptionWithPicker
              style={{ cursor: "pointer" }}
              onClick={(e) => handleOpenSubscriptionModal()}
            >
              <OptionWithPickerText>
                {t('profile.subscription')}
              </OptionWithPickerText>
              <Arrow style={{ marginLeft: "auto" }} />
            </OptionWithPicker>
          </GroupContentWrapper>
          <GroupContentWrapper style={{ padding: 0 }}>
            <DeleteConfirmationChild
              open={deleteConfirmationOpen}
              setOpen={setDeleteConfirmationOpen}
              variant={"remove_account"}
              action={handleDeleteAccount}
            >
              <SettingsElement
                text={t('profile.delete_account')}
                icon={<Delete />}
                reverse
                customColor={({ theme }) => theme.menu.deleteColor}
                onClick={() => openDeleteReasonModal()}
              />
            </DeleteConfirmationChild>
          </GroupContentWrapper>
        </Content>
      </ContentWrapper>

      <input
        type="file"
        hidden
        accept=".png, .jpg, .jpeg "
        ref={profileAvatarRef}
        onChange={(e) => handleAvatarChange(e)}
      />
    </Container>
  );
};

export default AccountSettings;

const StyledInfoButton = styled(IconButton)`
  margin-left: auto;
  color: ${({ theme }) => theme.menu.infoText};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.menu.background};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;
const HeaderTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  background: ${({ theme }) => theme.menu.background};
  height: 100%;
  overflow: auto;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: 0 auto;
  max-width: 631px;
  width: 100%;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Line = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

const GroupContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
  padding: 8px 16px;
`;

const GroupTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.04px;
  vertical-align: middle;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const CompanyDetails = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.04px;
  vertical-align: middle;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;
const GroupSubtitle = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.04px;
  vertical-align: middle;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
const CustomTextArea = styled.textarea`
  width: 100%;
  resize: none;
  background: none;
  min-height: 22px;
  overflow: hidden;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 8px 0;
  letter-spacing: -0.41px;
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    opacity: 0.6;
    font-weight: 400;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
`;
const Status = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 0 0 0;
  p {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
  }
`;
const CustomInput = styled.input`
  width: 100%;
  background: none;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.41px;
  &:focus-visible {
    outline: none;
  }
`;
const UserOption = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  padding-bottom: 8px;
  gap: 10px;
  align-items: start;
`;
const UserAvatar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  background: ${({ selected, theme }) =>
    selected ? theme.sidebar.selectedBtnBackground : "transparent"};
  color: ${({ selected, theme }) =>
    selected ? theme.sidebar.selectedIconColor : theme.sidebar.iconColor};
  svg {
    width: 54px;
    height: 54px;
  }
`;

const AvatarWrapper = styled.div`
  object-fit: cover;
  width: 54px;
  height: 54px;
  position: relative;
`;
const ProWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: -5px;
  right: -5px;
  height: 22px;
  width: 22px;
  svg {
    color: ${({ theme }) => theme.loginButton.background};
  }
`;

const EditButton = styled.p`
  cursor: pointer;
  color: ${({ theme }) => theme.primaryButton.color};
  font-weight: 600;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: -0.41px;
  text-align: center;
`;
const OptionWithPickerText = styled.p`
  min-width: 100px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: -0.41px;
`;
const OptionWithPicker = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  padding: 8px 0;
`;
const Separator = styled.div`
  display: flex;
  width: 100%;
  min-height: 1px;
  background: ${({ theme }) => theme.sidebar.borderColor};
  margin: 8px 0;
`;
const BusinessText = styled.span`
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-weight: 600;
`;

const LinkPhoneNumberBtn = styled.p`
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  cursor: pointer;
  white-space: nowrap;
`;
